import React, { useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddDurationPage = () => {

  // =========================================
  // NAVIGATE
  // =========================================

  const navigate = useNavigate();

  // =========================================
  // DURATION ROWS
  // =========================================

  const [durationRows, setDurationRows] = useState([
    {
      days: "",
      nights: "",
    },
  ]);

  // =========================================
  // ADD ROW
  // =========================================

  const addDurationRow = () => {

    setDurationRows((prevRows) => [
      ...prevRows,
      {
        days: "",
        nights: "",
      },
    ]);

  };

  // =========================================
  // REMOVE ROW
  // =========================================

  const removeDurationRow = async (index) => {

    // At least one row should remain
    if (durationRows.length === 1) {

      toast.error("At least one row is required");

      return;
    }

    // Confirmation
    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to remove this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setDurationRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================================
  // HANDLE INPUT CHANGE
  // =========================================

  const handleDurationChange = (
    index,
    field,
    value
  ) => {

    setDurationRows((prevRows) => {

      const updatedRows = [...prevRows];

      updatedRows[index] = {
        ...updatedRows[index],
        [field]: value,
      };

      return updatedRows;
    });

  };

  // =========================================
  // VALIDATE DURATION ROWS
  // =========================================

  const validateDurationRows = () => {

    // =========================================
    // CHECK DAYS
    // =========================================

    const hasEmptyDays = durationRows.some(
      (row) => !row?.days
    );

    if (hasEmptyDays) {

      toast.error("Days is required");

      return false;
    }

    // =========================================
    // CHECK NIGHTS
    // =========================================

    const hasEmptyNights = durationRows.some(
      (row) => !row?.nights
    );

    if (hasEmptyNights) {

      toast.error("Nights is required");

      return false;
    }

    // =========================================
    // CHECK DAYS / NIGHTS LOGIC
    // =========================================

    const hasInvalidDuration = durationRows.some(
      (row) => {

        const days = Number(row.days);
        const nights = Number(row.nights);

        return nights >= days;
      }
    );

    if (hasInvalidDuration) {

      toast.error(
        "Nights should be less than Days"
      );

      return false;
    }

    // =========================================
    // CHECK DUPLICATE DURATION
    // =========================================

    const durationMap = new Map();

    for (const row of durationRows) {

      const key = `${row.days}-${row.nights}`;

      if (durationMap.has(key)) {

        toast.error(
          `Duplicate duration ${row.days} Days / ${row.nights} Nights`
        );

        return false;
      }

      durationMap.set(key, true);
    }

    return true;
  };

  // =========================================
  // SAVE DURATION
  // =========================================

 const saveDuration = async () => {

  // =========================================
  // VALIDATION
  // =========================================

  if (!Array.isArray(durationRows) || durationRows.length === 0) {
    toast.error("Duration is required");
    return;
  }

  // Check every row
  const hasEmptyRow = durationRows.some(
    (row) =>
      String(row?.days ?? "").trim() === "" ||
      String(row?.nights ?? "").trim() === ""
  );

  if (hasEmptyRow) {
    toast.error("Days and Nights are required");
    return;
  }

  // Validate numeric values
  const hasInvalidValue = durationRows.some(
    (row) =>
      isNaN(Number(row.days)) ||
      isNaN(Number(row.nights))
  );

  if (hasInvalidValue) {
    toast.error("Days and Nights must be valid numbers");
    return;
  }

  // Validate greater than 0
  const hasInvalidNumber = durationRows.some(
    (row) =>
      Number(row.days) <= 0 ||
      Number(row.nights) < 0
  );

  if (hasInvalidNumber) {
    toast.error(
      "Days must be greater than 0 and Nights cannot be negative"
    );
    return;
  }

  // =========================================
  // CONFIRMATION
  // =========================================

  const confirm = await Swal.fire({
    title: "Save Duration?",
    text: "Do you want to save these Durations?",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Yes, Save",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#0d6efd",
  });

  if (!confirm.isConfirmed) {
    return;
  }

  try {

    // =========================================
    // CLEAN DATA
    // =========================================

    const durations = durationRows.map((row) => ({
      days: Number(row.days),
      nights: Number(row.nights),
    }));

    // =========================================
    // PAYLOAD
    // =========================================

    const payload = {
      durations,
    };

    console.log(
      "DURATION PAYLOAD =>",
      payload
    );

    // =========================================
    // API CALL
    // =========================================

    const res = await api.post(
      "/adminHolidays/holidays-duration-save",
      payload
    );

    console.log(
      "DURATION SAVE RESPONSE =>",
      res?.data
    );

    // =========================================
    // SUCCESS
    // =========================================

    if (res?.data?.success) {

      await Swal.fire({
        title: "Success",
        text:
          res?.data?.message ||
          "Duration Saved Successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      // =========================================
      // RESET
      // =========================================

      setDurationRows([
        {
          days: "",
          nights: "",
        },
      ]);

      // =========================================
      // NAVIGATE
      // =========================================

      navigate(
        "/holidays/duration/manage-duration"
      );

    } else {

      await Swal.fire({
        title: "Error",
        text:
          res?.data?.message ||
          "Unable to save Duration",
        icon: "error",
        confirmButtonText: "OK",
      });

    }

  } catch (error) {

    console.error(
      "Save Duration Error =>",
      error
    );

    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Server Error";

    toast.error(errorMessage);
  }
};

  // =========================================
  // JSX
  // =========================================

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* =========================================
          HEADER
      ========================================= */}

      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">

        <div className="card-body d-flex justify-content-between align-items-center">

          <h4 className="fw-bold mb-0">
            Add Duration
          </h4>

          <Link
            to="/holidays/duration/manage-duration"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </Link>

        </div>

      </div>

      {/* =========================================
          TABLE
      ========================================= */}

      <div className="row">

        <div className="col-lg-12">

          <div className="card shadow-sm border-0">

            <div className="card-body">

              <div className="table-responsive">

                <table className="table table-hover table-bordered align-middle mb-0">

                  {/* =========================================
                      TABLE HEADER
                  ========================================= */}

                  <thead className="table-light">

                    <tr>

                      <th
                        width="70"
                        className="text-center"
                      >
                        Sl.No
                      </th>

                      <th
                        className="text-center"
                      >
                        Days
                      </th>

                      <th
                        className="text-center"
                      >
                        Nights
                      </th>

                      <th
                        width="140"
                        className="text-center"
                      >
                        Action
                      </th>

                    </tr>

                  </thead>

                  {/* =========================================
                      TABLE BODY
                  ========================================= */}

                  <tbody>

                    {durationRows.map(
                      (row, index) => (

                        <tr key={index}>

                          {/* =================================
                              SERIAL NUMBER
                          ================================= */}

                          <td className="text-center fw-semibold">
                            {index + 1}
                          </td>

                          {/* =================================
                              DAYS
                          ================================= */}

                          <td>

                            <select
                              className="form-control"
                              value={row.days}
                              onChange={(e) =>
                                handleDurationChange(
                                  index,
                                  "days",
                                  e.target.value
                                )
                              }
                            >

                              <option value="">
                                Select Days
                              </option>

                              <option value="0">
                                0 Day
                              </option>

                              <option value="1">
                                1 Day
                              </option>

                              <option value="2">
                                2 Days
                              </option>

                              <option value="3">
                                3 Days
                              </option>

                              <option value="4">
                                4 Days
                              </option>

                              <option value="5">
                                5 Days
                              </option>

                              <option value="6">
                                6 Days
                              </option>

                              <option value="7">
                                7 Days
                              </option>

                              <option value="8">
                                8 Days
                              </option>

                              <option value="9">
                                9 Days
                              </option>

                              <option value="10">
                                10 Days
                              </option>

                            </select>

                          </td>

                          {/* =================================
                              NIGHTS
                          ================================= */}

                          <td>

                            <select
                              className="form-control"
                              value={row.nights}
                              onChange={(e) =>
                                handleDurationChange(
                                  index,
                                  "nights",
                                  e.target.value
                                )
                              }
                            >

                              <option value="">
                                Select Nights
                              </option>

                              <option value="0">
                                0 Nights
                              </option>

                              <option value="1">
                                1 Night
                              </option>

                              <option value="2">
                                2 Nights
                              </option>

                              <option value="3">
                                3 Nights
                              </option>

                              <option value="4">
                                4 Nights
                              </option>

                              <option value="5">
                                5 Nights
                              </option>

                              <option value="6">
                                6 Nights
                              </option>

                              <option value="7">
                                7 Nights
                              </option>

                              <option value="8">
                                8 Nights
                              </option>

                              <option value="9">
                                9 Nights
                              </option>

                              <option value="10">
                                10 Nights
                              </option>

                            </select>

                          </td>

                          {/* =================================
                              ACTION
                          ================================= */}

                          <td className="text-center">

                            {/* ADD */}

                            <button
                              type="button"
                              className="btn btn-success btn-sm me-2"
                              onClick={addDurationRow}
                              title="Add Row"
                            >
                              <i className="fa fa-plus-square"></i>
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                removeDurationRow(index)
                              }
                              title="Delete Row"
                            >
                              <i className="fa fa-trash"></i>
                            </button>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>

        </div>

        {/* =========================================
            SAVE BUTTON
        ========================================= */}

        <div className="col-lg-12 text-center">

          <button
            type="button"
            className="btn btn-info text-white px-5 mt-3"
            onClick={saveDuration}
          >

            <i className="fa fa-save me-2"></i>

            Save Duration

          </button>

        </div>

      </div>

    </div>
  );
};

export default AddDurationPage;